import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import VerifiedSkillsEnterpriseProgramForCourseQuery from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/VerifiedSkillsEnterpriseProgramForCourseQuery.graphql';
import {
  type VerifiedSkillsEnterpriseProgramForCourseQuery as QueryResult,
  type VerifiedSkillsEnterpriseProgramForCourseQueryVariables as QueryVariables,
  type VerifiedSkillsProgramCurriculumProductsV1Fragment,
} from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseProgramForCourseQuery';
import { getVerifiedSkillsPilotOrgIds } from 'bundles/skills-common/shared/verified-skills/utils/verifiedSkillsUtils';

export type Props = {
  userId: number;
  courseId: string;
  skip?: boolean;
};

/**
 * Finds an enterprise program that matches verified skills pilot organization IDs
 * @param {VerifiedSkillsProgramCurriculumProductsV1Fragment[]|null|undefined} programs - Array of enterprise programs to search
 * @param {string[]} pilotOrgIds - Array of pilot organization IDs to match against
 * @returns {VerifiedSkillsProgramCurriculumProductsV1Fragment|undefined|null} The first matching program or undefined/null if no programs exist
 */
const findPilotProgram = (
  programs: (VerifiedSkillsProgramCurriculumProductsV1Fragment | null)[] | null | undefined,
  pilotOrgIds: string[]
): VerifiedSkillsProgramCurriculumProductsV1Fragment | undefined | null => {
  if (!programs?.length || !pilotOrgIds?.length) {
    return undefined;
  }

  const pilotOrgIdSet = new Set(pilotOrgIds);

  return programs.find(
    (program) =>
      program?.program?.thirdPartyOrganizationId && pilotOrgIdSet.has(program?.program.thirdPartyOrganizationId)
  );
};

/**
 * Hook to get enterprise program data for a specific user and course
 * @param {Object} params - Hook parameters
 * @param {number} params.userId - The user ID
 * @param {string} params.courseId - The course ID
 * @returns {Object} Hook result object
 * @returns {boolean} returns.loading - Whether the GraphQL query is loading
 * @returns {boolean} returns.isEnterpriseUser - Whether the user is enrolled in enterprise programs
 * @returns {VerifiedSkillsProgramCurriculumProductsV1Fragment['program']|null|undefined} returns.program - The matching pilot program, or first program as fallback, null, or undefined
 */
const useGetEnterpriseProgramForCourse = ({ userId, courseId, skip }: Props) => {
  const {
    loading,
    error,
    data: programData,
  } = useQuery<QueryResult, QueryVariables>(VerifiedSkillsEnterpriseProgramForCourseQuery, {
    variables: {
      userId: userId.toString(),
      courseId,
    },
    skip,
  });

  const enrollmentData =
    programData?.NaptimeQueries?.ProgramCurriculumProductsV1Resource?.enrollmentByUserAndCourse?.elements;
  const isEnterpriseUser = (enrollmentData?.length || 0) > 0;

  const program = useMemo(() => {
    const verifiedSkillsPilotOrgIds = getVerifiedSkillsPilotOrgIds();
    const foundProgram = findPilotProgram(enrollmentData, verifiedSkillsPilotOrgIds);

    // Fallback to the first program if no programs are part of the pilot
    return foundProgram?.program || enrollmentData?.[0]?.program;
  }, [enrollmentData]);

  if (error) {
    Sentry.captureException(error);

    return {
      isEnterpriseUser: false,
      program: null,
      loading: false,
    };
  }

  return {
    isEnterpriseUser,
    program,
    loading,
  };
};

export default useGetEnterpriseProgramForCourse;
